import { useState, useEffect } from 'react'
import { Box, HStack, Input } from '@chakra-ui/react'
import { Field } from '@chakra-ui/react'
import { NativeSelectRoot, NativeSelectField } from '@chakra-ui/react'

interface FractionInputProps {
  value: number // Percentage (0-100) for display
  onChange: (numerator: number, denominator: number) => void // Returns fraction
  label?: string
  error?: string
  disabled?: boolean
}

interface Fraction {
  display: string
  numerator: number
  denominator: number
  percentage: number
}

const COMMON_FRACTIONS: Fraction[] = [
  { display: '1/2', numerator: 1, denominator: 2, percentage: 50 },
  { display: '1/3', numerator: 1, denominator: 3, percentage: 33.33 },
  { display: '2/3', numerator: 2, denominator: 3, percentage: 66.67 },
  { display: '1/4', numerator: 1, denominator: 4, percentage: 25 },
  { display: '3/4', numerator: 3, denominator: 4, percentage: 75 },
  { display: '1/5', numerator: 1, denominator: 5, percentage: 20 },
  { display: '2/5', numerator: 2, denominator: 5, percentage: 40 },
  { display: '3/5', numerator: 3, denominator: 5, percentage: 60 },
  { display: '4/5', numerator: 4, denominator: 5, percentage: 80 },
  { display: '1/6', numerator: 1, denominator: 6, percentage: 16.67 },
  { display: '5/6', numerator: 5, denominator: 6, percentage: 83.33 },
  { display: 'Autre', numerator: -1, denominator: -1, percentage: -1 },
]

// Find closest fraction for a given percentage
function findClosestFraction(percentage: number): Fraction {
  if (percentage <= 0 && COMMON_FRACTIONS[0]) return COMMON_FRACTIONS[0]
  if (percentage >= 100) return { display: '1/1', numerator: 1, denominator: 1, percentage: 100 }

  let closest = COMMON_FRACTIONS[0]!
  let minDiff = Math.abs(percentage - closest.percentage)

  for (const fraction of COMMON_FRACTIONS) {
    if (fraction.percentage === -1) continue // Skip "Autre"
    const diff = Math.abs(percentage - fraction.percentage)
    if (diff < minDiff) {
      minDiff = diff
      closest = fraction
    }
  }

  // If difference is more than 2%, consider it custom
  if (minDiff > 2) {
    return { display: 'Autre', numerator: -1, denominator: -1, percentage: -1 }
  }

  return closest
}

// Parse fraction string like "1/2" to {numerator, denominator, percentage}
function parseFraction(fractionStr: string): { numerator: number; denominator: number; percentage: number } | null {
  const match = fractionStr.match(/^(\d+)\/(\d+)$/)
  if (!match || !match[1] || !match[2]) return null

  const numerator = parseInt(match[1], 10)
  const denominator = parseInt(match[2], 10)

  if (denominator === 0) return null

  const percentage = (numerator / denominator) * 100
  return { numerator, denominator, percentage }
}

export function FractionInput({
  value,
  onChange,
  label,
  error,
  disabled = false,
}: FractionInputProps) {
  const [selectedFraction, setSelectedFraction] = useState<Fraction>(
    findClosestFraction(value)
  )
  const [customInput, setCustomInput] = useState<string>('')
  const [customError, setCustomError] = useState<string>('')

  // Update selected fraction when value changes externally
  useEffect(() => {
    const closest = findClosestFraction(value)
    setSelectedFraction(closest)
    if (closest.display === 'Autre') {
      // Try to represent as a simple fraction
      setCustomInput(`${value.toFixed(2)}%`)
    }
  }, [value])

  const handleFractionChange = (fractionDisplay: string) => {
    const fraction = COMMON_FRACTIONS.find((f) => f.display === fractionDisplay)
    if (!fraction) return

    setSelectedFraction(fraction)

    if (fraction.percentage !== -1) {
      // Standard fraction selected - return numerator and denominator
      onChange(fraction.numerator, fraction.denominator)
      setCustomError('')
    } else {
      // "Autre" selected - keep current value or reset
      setCustomInput('')
    }
  }

  const handleCustomInputChange = (input: string) => {
    setCustomInput(input)
    setCustomError('')

    // Try to parse as fraction (e.g., "1/2")
    const fractionValue = parseFraction(input.trim())
    if (fractionValue !== null) {
      if (fractionValue.percentage < 0 || fractionValue.percentage > 100) {
        setCustomError('La valeur doit être entre 0% et 100%')
        return
      }
      onChange(fractionValue.numerator, fractionValue.denominator)
      return
    }

    // Try to parse as percentage (e.g., "45%", "45", "45.5")
    const percentMatch = input.trim().match(/^(\d+(?:\.\d+)?)\s*%?$/)
    if (percentMatch && percentMatch[1]) {
      const percentValue = parseFloat(percentMatch[1])
      if (percentValue < 0 || percentValue > 100) {
        setCustomError('La valeur doit être entre 0% et 100%')
        return
      }
      // Convert percentage to fraction (simplified approximation)
      // For exact conversion, we'd need a more sophisticated algorithm
      // For now, we'll use a simple approach: N/100
      const numerator = Math.round(percentValue)
      const denominator = 100
      onChange(numerator, denominator)
      return
    }

    // Invalid input
    if (input.trim() !== '') {
      setCustomError('Format invalide. Utilisez "1/2" ou "50%" ou "50"')
    }
  }

  const handleCustomInputBlur = () => {
    // If input is empty or invalid, reset to current value
    if (customInput.trim() === '' || customError) {
      setCustomInput(`${value.toFixed(2)}%`)
      setCustomError('')
    }
  }

  return (
    <Field.Root invalid={!!error || !!customError}>
      {label && <Field.Label>{label}</Field.Label>}
      <HStack gap={2} align="start">
        <Box flex={1}>
          <NativeSelectRoot>
            <NativeSelectField
              value={selectedFraction.display}
              onChange={(e) => handleFractionChange(e.target.value)}
              {...(disabled ? { _disabled: { opacity: 0.5, cursor: 'not-allowed' } } : {})}
            >
              {COMMON_FRACTIONS.map((fraction) => (
                <option key={fraction.display} value={fraction.display}>
                  {fraction.display}
                  {fraction.percentage !== -1 && ` (${fraction.percentage.toFixed(2)}%)`}
                </option>
              ))}
            </NativeSelectField>
          </NativeSelectRoot>
        </Box>

        {selectedFraction.display === 'Autre' && (
          <Box flex={1}>
            <Input
              value={customInput}
              onChange={(e) => handleCustomInputChange(e.target.value)}
              onBlur={handleCustomInputBlur}
              placeholder="1/2 ou 50%"
              size="sm"
              disabled={disabled}
              borderColor={customError ? 'red.500' : undefined}
            />
          </Box>
        )}

        {selectedFraction.display !== 'Autre' && (
          <Box flex={1}>
            <Input
              value={`${value.toFixed(2)}%`}
              size="sm"
              disabled
              bg="gray.50"
              color="gray.600"
            />
          </Box>
        )}
      </HStack>
      {(error || customError) && (
        <Field.ErrorText>{error || customError}</Field.ErrorText>
      )}
      {!error && !customError && selectedFraction.display !== 'Autre' && (
        <Field.HelperText>
          {selectedFraction.display} = {value.toFixed(2)}%
        </Field.HelperText>
      )}
    </Field.Root>
  )
}
